/**
 * Create / edit Group Template (HealthcareService category=group-template).
 * Session HCS is auto-stamped on save; screening HCS is not linked.
 */
import {
  Avatar,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconPhotoUp } from '@tabler/icons-react';
import { useEffect, useState, type ChangeEvent } from 'react';

import {
  DAY_OF_WEEK_CODES,
  DAY_OF_WEEK_CODE_TO_WEEKDAY_NAME,
  type DayOfWeekCode,
} from '@unify/fhir-model/resources/schedule';

import { useResolvedImageUrl } from '@/hooks/useResolvedImageUrl';
import {
  COMMON_TIMEZONE_OPTIONS,
  ENROLLMENT_MODE_OPTIONS,
  type GroupTemplateFormValues,
} from '@/lib/groupTherapy';

const MODALITY_OPTIONS = [
  { value: 'telehealth', label: 'Telehealth' },
  { value: 'in-person', label: 'In person' },
  { value: 'hybrid', label: 'Hybrid' },
] as const;

const DAY_OF_WEEK_OPTIONS = DAY_OF_WEEK_CODES.map((code) => ({
  value: code,
  label: DAY_OF_WEEK_CODE_TO_WEEKDAY_NAME[code],
}));

const DEFAULTS: GroupTemplateFormValues = {
  active: true,
  name: '',
  enrollmentMode: 'closed',
  minEnrollment: 4,
  maxEnrollment: 10,
  timezone: 'America/Los_Angeles',
  modality: 'telehealth',
  sessionCount: 8,
  dayOfWeek: 'tue',
};

export interface GroupTemplateFormModalProps {
  opened: boolean;
  title: string;
  initialValues?: GroupTemplateFormValues;
  practiceOptions: { value: string; label: string }[];
  showPracticeSelect?: boolean;
  defaultOrganizationId?: string;
  defaultOrganizationName?: string;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (values: GroupTemplateFormValues, imageFile?: File | null) => void | Promise<void>;
}

export function GroupTemplateFormModal({
  opened,
  title,
  initialValues,
  practiceOptions,
  showPracticeSelect = false,
  defaultOrganizationId,
  defaultOrganizationName,
  saving = false,
  onClose,
  onSubmit,
}: GroupTemplateFormModalProps): JSX.Element {
  const [values, setValues] = useState<GroupTemplateFormValues>(DEFAULTS);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
  const [savedImageUrl, setSavedImageUrl] = useState<string | undefined>();
  const [hoverPhoto, setHoverPhoto] = useState(false);

  const resolvedSaved = useResolvedImageUrl(savedImageUrl);
  const previewSrc = pendingPreviewUrl ?? resolvedSaved;

  useEffect(() => {
    if (!opened) return;
    const next = { ...DEFAULTS, ...initialValues };
    if (!next.organizationId && defaultOrganizationId) {
      next.organizationId = defaultOrganizationId;
      next.organizationName = defaultOrganizationName;
    }
    setValues(next);
    setSavedImageUrl(next.imageUrl);
    setPendingImageFile(null);
    setPendingPreviewUrl(null);
    setErrors({});
  }, [opened, initialValues, defaultOrganizationId, defaultOrganizationName]);

  useEffect(() => {
    return () => {
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
    };
  }, [pendingPreviewUrl]);

  const patch = (partial: Partial<GroupTemplateFormValues>) => {
    setValues((prev) => ({ ...prev, ...partial }));
  };

  const onPickImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
    setPendingImageFile(file);
    setPendingPreviewUrl(URL.createObjectURL(file));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!values.name.trim()) next.name = 'Name is required.';
    if (!values.organizationId) next.organizationId = 'Practice is required.';
    if (!values.minEnrollment || values.minEnrollment <= 0) {
      next.minEnrollment = 'Minimum enrollment must be greater than 0.';
    }
    if (!values.maxEnrollment || values.maxEnrollment < values.minEnrollment) {
      next.maxEnrollment = 'Maximum must be ≥ minimum.';
    }
    if (!values.sessionCount || values.sessionCount < 1) {
      next.sessionCount = 'Appointments in series must be at least 1.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" centered>
      <Stack gap="md">
        <Stack align="center" gap="xs">
          <Box
            style={{ position: 'relative' }}
            onMouseEnter={() => setHoverPhoto(true)}
            onMouseLeave={() => setHoverPhoto(false)}
          >
            <UnstyledButton component="label" style={{ display: 'block', cursor: 'pointer' }}>
              <Avatar
                src={previewSrc || undefined}
                radius="md"
                size={160}
                color="gold"
                style={{
                  border: `2px dashed ${
                    hoverPhoto
                      ? 'var(--mantine-color-gold-5)'
                      : 'var(--mantine-color-gray-4)'
                  }`,
                  cursor: 'pointer',
                }}
              >
                {!previewSrc && (
                  <Stack align="center" gap={4}>
                    <IconPhotoUp size={40} style={{ opacity: 0.6 }} />
                    <Text size="sm" c="gray.6">
                      Upload photo
                    </Text>
                  </Stack>
                )}
              </Avatar>
              <input type="file" accept="image/*" hidden onChange={onPickImage} />
            </UnstyledButton>
          </Box>
        </Stack>
        <TextInput
          label="Template name"
          required
          value={values.name}
          error={errors.name}
          onChange={(e) => patch({ name: e.currentTarget.value })}
        />
        <Textarea
          label="Description"
          minRows={2}
          value={values.comment ?? ''}
          onChange={(e) => patch({ comment: e.currentTarget.value })}
        />

        {showPracticeSelect ? (
          <Select
            label="Practice"
            required
            data={practiceOptions}
            value={values.organizationId ?? null}
            error={errors.organizationId}
            onChange={(v) => {
              const opt = practiceOptions.find((o) => o.value === v);
              patch({ organizationId: v ?? undefined, organizationName: opt?.label });
            }}
            searchable
          />
        ) : (
          <Text size="sm" c="dimmed">
            Practice: {values.organizationName ?? defaultOrganizationName ?? '—'}
          </Text>
        )}

        <Select
          label="Enrollment mode"
          data={ENROLLMENT_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={values.enrollmentMode}
          onChange={(v) => patch({ enrollmentMode: (v as 'closed' | 'open') ?? 'closed' })}
        />
        <Group grow>
          <NumberInput
            label="Min capacity"
            min={1}
            value={values.minEnrollment}
            error={errors.minEnrollment}
            onChange={(v) => patch({ minEnrollment: Number(v) || 1 })}
          />
          <NumberInput
            label="Max capacity"
            min={1}
            value={values.maxEnrollment}
            error={errors.maxEnrollment}
            onChange={(v) => patch({ maxEnrollment: Number(v) || 1 })}
          />
        </Group>
        <Group grow>
          <Select
            label="Default modality"
            data={[...MODALITY_OPTIONS]}
            value={values.modality ?? null}
            onChange={(v) =>
              patch({ modality: (v as GroupTemplateFormValues['modality']) ?? undefined })
            }
          />
          <NumberInput
            label="Appointments in series"
            min={1}
            max={365}
            value={values.sessionCount ?? 8}
            error={errors.sessionCount}
            onChange={(v) => patch({ sessionCount: Number(v) || 1 })}
          />
        </Group>
        <Select
          label="Default day of week"
          data={DAY_OF_WEEK_OPTIONS}
          value={values.dayOfWeek ?? null}
          onChange={(v) => patch({ dayOfWeek: (v as DayOfWeekCode | null) ?? undefined })}
          clearable
        />
        <Select
          label="Default timezone"
          data={COMMON_TIMEZONE_OPTIONS}
          value={values.timezone ?? null}
          onChange={(v) => patch({ timezone: v ?? undefined })}
          searchable
        />
        <Switch
          label="Active"
          checked={values.active}
          onChange={(e) => patch({ active: e.currentTarget.checked })}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            loading={saving}
            onClick={() => {
              if (!validate()) return;
              void onSubmit(values, pendingImageFile);
            }}
          >
            Save template
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
